/**
 * SubscriptionsPage.tsx — Enterprise subscription management
 *
 * Lists all subscriptions with resolved customer/plan names, status badges,
 * and action buttons (cancel, view details). Uses the admin-scoped endpoint.
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import DataTable, { type Column } from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import BulkActionBar from '../components/BulkActionBar';
import SlidePanel from '../components/SlidePanel';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';

interface AdminSubscription {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  plan_id: string;
  plan_name: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  canceled_at: string | null;
  created_at: string;
}

function useAdminSubscriptionsList(statusFilter?: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  const params = new URLSearchParams();
  if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
  return useQuery<AdminSubscription[]>({
    queryKey: ['admin-subscriptions', statusFilter],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/subscriptions?${params.toString()}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load subscriptions: ${res.status}`);
      const json = await res.json();
      return json.data;
    },
    enabled: !!token,
  });
}

function useCancelSubscription() {
  const { token, apiBaseUrl } = useAdminAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, immediate }: { id: string; immediate: boolean }) => {
      const res = await fetch(`${apiBaseUrl}/admin/subscriptions/${id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ immediate }),
      });
      if (!res.ok) throw new Error(`Cancel failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    },
  });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-800',
    trialing: 'bg-violet-100 text-violet-800',
    canceled: 'bg-slate-100 text-slate-600',
    past_due: 'bg-amber-100 text-amber-800',
    unpaid: 'bg-red-100 text-red-700',
    paused: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${styles[status] ?? 'bg-slate-100 text-slate-700'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export default function SubscriptionsPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [cancelTarget, setCancelTarget] = useState<AdminSubscription | null>(null);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [detailSub, setDetailSub] = useState<AdminSubscription | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCanceling, setBulkCanceling] = useState(false);
  const [editPlanId, setEditPlanId] = useState('');
  const [prorationBehavior, setProrationBehavior] = useState<'create_prorations' | 'none' | 'always_invoice'>('create_prorations');
  const [trialDays, setTrialDays] = useState('');
  const [updateResult, setUpdateResult] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  const { data: subscriptions, isLoading, error } = useAdminSubscriptionsList(filterStatus);
  const cancelMutation = useCancelSubscription();
  const queryClient = useQueryClient();

  // Fetch plans for the plan change dropdown
  const { data: plans } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['admin-plans-for-subs'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data ?? json).map((p: { id: string; name: string }) => ({ id: p.id, name: p.name }));
    },
    enabled: !!token,
  });

  async function handleUpdateSubscription(): Promise<void> {
    if (!detailSub) return;
    setUpdating(true);
    setUpdateResult(null);
    try {
      const body: Record<string, unknown> = {};
      if (editPlanId && editPlanId !== detailSub.plan_id) {
        body.planId = editPlanId;
        body.prorationBehavior = prorationBehavior;
      }
      if (trialDays) {
        const days = parseInt(trialDays, 10);
        if (days > 0) body.trialEnd = Math.floor(Date.now() / 1000) + (days * 24 * 60 * 60);
      }
      if (Object.keys(body).length === 0) {
        setUpdateResult('No changes to save.');
        return;
      }
      const res = await fetch(`${apiBaseUrl}/admin/subscriptions/${detailSub.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      setUpdateResult('✓ Subscription updated and synced to Stripe.');
      void queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    } catch (err) {
      setUpdateResult(`✗ ${err instanceof Error ? err.message : 'Update failed'}`);
    } finally {
      setUpdating(false);
    }
  }

  const activeCount = (subscriptions ?? []).filter((s) => s.status === 'active').length;
  const trialCount = (subscriptions ?? []).filter((s) => s.status === 'trialing').length;

  async function handleCancel(): Promise<void> {
    if (!cancelTarget) return;
    await cancelMutation.mutateAsync({ id: cancelTarget.id, immediate: cancelImmediate });
    setCancelTarget(null);
    setCancelImmediate(false);
  }

  async function handleBulkCancel(): Promise<void> {
    setBulkCanceling(true);
    try {
      await Promise.all([...selected].map((id) => cancelMutation.mutateAsync({ id, immediate: false })));
      setSelected(new Set());
      setBulkCancelOpen(false);
    } finally { setBulkCanceling(false); }
  }

  const columns: Column<AdminSubscription>[] = [
    {
      key: 'customer_name',
      header: 'Customer',
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900">{row.customer_name}</p>
          <p className="text-xs text-slate-500">{row.customer_email}</p>
        </div>
      ),
    },
    {
      key: 'plan_name',
      header: 'Plan',
      render: (row) => <span className="font-medium text-slate-800">{row.plan_name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={row.status} />
          {row.cancel_at_period_end && (
            <span className="text-[10px] text-amber-600 font-medium">Cancels at period end</span>
          )}
        </div>
      ),
    },
    {
      key: 'current_period_end',
      header: 'Period End',
      render: (row) => <span className="text-sm text-slate-700">{formatDate(row.current_period_end)}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => <span className="text-sm text-slate-500">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDetailSub(row)}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Details
          </button>
          {(row.status === 'active' || row.status === 'trialing') && !row.cancel_at_period_end && (
            <button
              onClick={() => setCancelTarget(row)}
              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusFilters = ['all', 'active', 'trialing', 'past_due', 'canceled', 'paused'];

  return (
    <AdminLayout title="Subscriptions">
      <div className="space-y-6">
        {/* Header */}
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Billing lifecycle</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Subscription Management</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                View all customer subscriptions, monitor statuses, and manage cancellations. Subscriptions are automatically linked to their customer and plan records.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Total</p>
                <p className="text-xl font-semibold text-slate-900">{(subscriptions ?? []).length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-emerald-600">Active</p>
                <p className="text-xl font-semibold text-emerald-700">{activeCount}</p>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-violet-600">Trialing</p>
                <p className="text-xl font-semibold text-violet-700">{trialCount}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Status filters */}
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((f) => (
            <button
              key={f}
              onClick={() => setFilterStatus(f)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                filterStatus === f
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error.message}
          </div>
        )}

        {selected.size > 0 && (
          <BulkActionBar
            count={selected.size}
            label="subscription"
            onDelete={() => setBulkCancelOpen(true)}
            onClear={() => setSelected(new Set())}
            deleting={bulkCanceling}
          />
        )}

        <DataTable<AdminSubscription>
          columns={columns}
          rows={subscriptions ?? []}
          rowKey={(row) => row.id}
          loading={isLoading}
          emptyMessage="No subscriptions found."
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(row) => setDetailSub(row)}
        />
      </div>

      {/* Detail slide panel */}
      <SlidePanel
        open={detailSub !== null}
        onClose={() => setDetailSub(null)}
        size="md"
        title={detailSub?.customer_name}
        subtitle={detailSub?.plan_name}
        category="Subscription Detail"
      >
        {detailSub && (
          <div className="px-6 py-6 space-y-6">
            <div className="grid gap-3 grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Plan</p>
                <p className="mt-1.5 font-medium text-slate-900">{detailSub.plan_name}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Status</p>
                <p className="mt-1.5"><StatusBadge status={detailSub.status} /></p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Period Start</p>
                <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailSub.current_period_start)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Period End</p>
                <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailSub.current_period_end)}</p>
              </div>
              {detailSub.trial_end && (
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Trial Ends</p>
                  <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailSub.trial_end)}</p>
                </div>
              )}
              {detailSub.canceled_at && (
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Canceled At</p>
                  <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailSub.canceled_at)}</p>
                </div>
              )}
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Created</p>
                <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailSub.created_at)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Customer</p>
                <p className="mt-1.5 text-sm text-slate-700">{detailSub.customer_email}</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Subscription ID</p>
              <p className="mt-1 font-mono text-xs text-slate-600">{detailSub.id}</p>
            </div>

            {/* Edit section — plan change, trial extension */}
            {(detailSub.status === 'active' || detailSub.status === 'trialing') && (
              <div className="space-y-4 rounded-xl border border-slate-200 p-4">
                <p className="text-sm font-semibold text-slate-700">Edit Subscription</p>

                {/* Plan change */}
                <div>
                  <label className="text-xs text-slate-500">Change Plan</label>
                  <select
                    value={editPlanId || detailSub.plan_id}
                    onChange={(e) => setEditPlanId(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value={detailSub.plan_id}>{detailSub.plan_name} (current)</option>
                    {(plans ?? []).filter(p => p.id !== detailSub.plan_id).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Proration behavior */}
                {editPlanId && editPlanId !== detailSub.plan_id && (
                  <div>
                    <label className="text-xs text-slate-500">Proration Behavior</label>
                    <select
                      value={prorationBehavior}
                      onChange={(e) => setProrationBehavior(e.target.value as 'create_prorations' | 'none' | 'always_invoice')}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="create_prorations">Create prorations (default)</option>
                      <option value="none">No proration</option>
                      <option value="always_invoice">Always invoice immediately</option>
                    </select>
                  </div>
                )}

                {/* Trial extension */}
                <div>
                  <label className="text-xs text-slate-500">Extend Trial (days from now)</label>
                  <input
                    type="number"
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    placeholder="0"
                    min="0"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>

                {updateResult && <p className="text-xs text-slate-600">{updateResult}</p>}

                <button
                  onClick={() => void handleUpdateSubscription()}
                  disabled={updating}
                  className="w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                >
                  {updating ? 'Updating…' : 'Save & Sync to Stripe'}
                </button>
              </div>
            )}

            {(detailSub.status === 'active' || detailSub.status === 'trialing') && !detailSub.cancel_at_period_end && (
              <button
                onClick={() => {
                  const sub = detailSub;
                  setDetailSub(null);
                  setCancelTarget(sub);
                }}
                className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        )}
      </SlidePanel>

      {/* Cancel confirm */}
      {cancelTarget && (
        <ConfirmDialog
          open={true}
          title="Cancel Subscription"
          message={
            <div>
              <p>Cancel subscription for <strong>{cancelTarget.customer_name}</strong> ({cancelTarget.plan_name})?</p>
              <label className="mt-4 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cancelImmediate}
                  onChange={(e) => setCancelImmediate(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Cancel immediately (otherwise cancels at period end)
              </label>
            </div>
          }
          confirmLabel="Cancel Subscription"
          cancelLabel="Keep Active"
          onConfirm={() => void handleCancel()}
          onCancel={() => { setCancelTarget(null); setCancelImmediate(false); }}
          loading={cancelMutation.isPending}
        />
      )}

      <ConfirmDialog
        open={bulkCancelOpen}
        title={`Cancel ${selected.size} Subscription${selected.size !== 1 ? 's' : ''}`}
        message={<p>Cancel <strong>{selected.size}</strong> subscription{selected.size !== 1 ? 's' : ''} at period end?</p>}
        confirmLabel="Cancel Subscriptions"
        cancelLabel="Keep Active"
        onConfirm={() => void handleBulkCancel()}
        onCancel={() => setBulkCancelOpen(false)}
        loading={bulkCanceling}
      />
    </AdminLayout>
  );
}
